import {
  commitMutation,
  readInlineData,
  useFragment,
  useLazyLoadQuery,
} from 'react-relay';
import { useCallback, useState, useEffect } from 'react';
import { EditProfileFormQuery } from 'graphql/__generated__/EditProfileFormQuery.graphql';
import { environment } from 'services/relay';
import Form, { Field, useFormState } from '@atlaskit/form';
import Image from '@atlaskit/image';
import SectionMessage from '@atlaskit/section-message';

import {
  EditProfileFormMutation,
  EditProfileFormMutation$data,
  UnifiedProfileInput,
} from 'graphql/__generated__/EditProfileFormMutation.graphql';
import { TextEditor } from 'components/common/text-editor/TextEditor';
import { Box } from '@atlaskit/primitives';
import { EDIT_PROFILE_FORM_QUERY } from 'graphql/EditProfileFormQuery';
import { CREATE_PROFILE_FORM_MUTATION } from 'graphql/EditProfileFormMutation';
import { css } from '@compiled/react';
import { ATLASSIAN_ACCOUNT_USER_FRAGMENT } from 'graphql/AtlassianAccountUserFragment';
import { UNIFIED_PROFILE_FRAGMENT } from 'graphql/UnifiedProfileFragment';
import { AtlassianAccountUserFragment$key } from 'graphql/__generated__/AtlassianAccountUserFragment.graphql';
import { UnifiedProfileFragment$key } from 'graphql/__generated__/UnifiedProfileFragment.graphql';
import { token } from '@atlaskit/tokens';
import {
  Button,
  ButtonAppearance,
  TextField,
  TextFieldLabel,
  TextFieldMessage,
  CommonModal,
  Text,
  Radio,
  UnifiedAccountTagPrimary,
} from '@atlassian/learning-components';
import { UnstyledButton } from 'components/common/UnstyledButton';
import { Offsite, ProfilePhoto } from 'assets/images';
import { InputValidator, useMapProducts } from 'utils';
import ManageProducts from './ManageProducts';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled
import { css as cssEmotion } from '@emotion/react';
import {
  Redirect,
  usePathParam,
  useRouterActions,
} from 'react-resource-router';
import useValidateProfileIdPath from 'utils/useValidateUsernamePath';
import useIsPersonalView from 'utils/useIsPersonalView';
import useIsProfilePersonalView from 'utils/useIsProfilePersonalView';
import { Products } from '../side-panel/products/Products';
import { getTextOnly } from 'utils/helper';
import { NormalizedProduct, useNormalizedProducts } from 'utils/useMapProducts';
import {
  AtlassianProductsFragment$data,
  AtlassianProductsFragment$key,
} from 'graphql/__generated__/AtlassianProductsFragment.graphql';
import { ATLASSIAN_PRODUCTS_FRAGMENT } from 'graphql/AtlassianProductsFragment';
import {
  sendScreenEvent,
  sendTrackEvent,
  sendUIEvent,
} from 'services/analytics';
import { PayloadError } from 'relay-runtime';
import { SCREEN_NAME, ATLASSIAN_ACCOUNT_USER_QUERY } from 'constants/index';
import { useEventErrorFlag } from 'utils/errors';
import { sendUserAction } from 'services/kms';
import { UNIFIED_ACCOUNT_QUERY } from 'graphql/UnifiedAccountQuery';
import { UnifiedAccountQuery } from 'graphql/__generated__/UnifiedAccountQuery.graphql';
import { AtlassianAccountUserQuery } from 'graphql/__generated__/AtlassianAccountUserQuery.graphql';
import { ATLASSIAN_ACCOUNT_ANY_USER_QUERY } from 'graphql/AtlassianAccountAnyUserQuery';
import { AtlassianAccountAnyUserQuery } from 'graphql/__generated__/AtlassianAccountAnyUserQuery.graphql';
type EditProfileFormValues = {
  products: string;
  role: string;
  company: string;
  location: string;
  bio: string;
  websiteUrl: string;
  linkedinUrl: string;
  youtubeUrl: string;
  xUrl: string;
  isPrivate: boolean;
};

const ProductsContent = ({
  allProducts,
  setHasSelectedProducts,
}: {
  allProducts: NormalizedProduct[];
  setHasSelectedProducts: (hasSelected: boolean) => void;
}) => {
  const formState = useFormState<EditProfileFormValues>({
    values: true,
  });
  const selectedProducts = formState?.values?.products
    ? useMapProducts(formState.values.products, allProducts)
    : [];

  // Informing "Add my products" button about if there are selected products
  useEffect(() => {
    setHasSelectedProducts(selectedProducts.length > 0);
  }, [selectedProducts]);

  if (!selectedProducts.length) {
    return (
      <p css={productsQuestionStyles}>
        {'What products are you currently working on?'}
      </p>
    );
  }
  return (
    <div css={productsContentStyles}>
      <Products
        products={selectedProducts}
        isPersonalView={true}
        source={SCREEN_NAME.EDIT_PROFILE}
      />
    </div>
  );
};

const EditProfileForm = () => {
  const [pathProfileId] = usePathParam('pathProfileId');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const openModal = useCallback(() => setIsModalOpen(true), []);
  const closeModal = useCallback(() => setIsModalOpen(false), []);
  const { push } = useRouterActions();
  const isValid = useValidateProfileIdPath();
  const isPersonalView = useIsPersonalView(pathProfileId as string);
  const isProfilePersonalView = useIsProfilePersonalView(
    pathProfileId as string,
  );
  const [hasSelectedProducts, setHasSelectedProducts] = useState(false);

  const addErrorFlag = useEventErrorFlag();

  const loggedInUser = useLazyLoadQuery<AtlassianAccountUserQuery>(
    ATLASSIAN_ACCOUNT_USER_QUERY,
    {},
    { fetchPolicy: 'store-or-network' },
  )?.me?.user;

  const accountId = loggedInUser?.accountId || '';
  const account = useLazyLoadQuery<UnifiedAccountQuery>(
    UNIFIED_ACCOUNT_QUERY,
    { aaid: accountId },
    { fetchPolicy: 'store-or-network' },
  )?.unified;

  const accountHasLinkedAccounts = account?.account?.isLinked;

  useEffect(() => {
    if (isValid && isPersonalView && pathProfileId) {
      sendScreenEvent({
        screenName: SCREEN_NAME.EDIT_PROFILE,
        isPersonalView,
      });
    }
  }, [isValid, isPersonalView, pathProfileId]);

  if (!isValid) {
    return <Redirect to="/error/user_not_found" />;
  }
  if (!isPersonalView) {
    return <Redirect to="/error/user_not_found" />;
  }

  const onClickBackToProfile = () => {
    sendUIEvent({
      source: SCREEN_NAME.EDIT_PROFILE,
      action: 'clicked',
      actionSubject: 'link',
      actionSubjectId: 'backToProfile',
      isPersonalView: true,
    });
    onFinishEdit();
  };

  const onClickClose = () => {
    sendUIEvent({
      source: SCREEN_NAME.EDIT_PROFILE,
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: 'closeEditProfile',
      isPersonalView: true,
    });
    onFinishEdit();
  };

  const onFinishEdit = useCallback(() => {
    push(`/profile/${pathProfileId}`);
  }, []);

  const getTrackAttributes = (data: EditProfileFormMutation$data) => {
    const profile = data?.unified?.updateUnifiedProfile?.unifiedProfile;
    return {
      hasBio: !!profile?.bio,
      hasCompany: !!profile?.company,
      hasLinkedinUrl: !!profile?.linkedinUrl,
      hasLocation: !!profile?.location,
      hasRole: !!profile?.role,
      hasWebsiteUrl: !!profile?.websiteUrl,
      hasXUrl: !!profile?.xUrl,
      hasYoutubeUrl: !!profile?.youtubeUrl,
      productCount: profile?.products ? profile?.products.split(',').length : 0,
    };
  };

  const onSuccess = (
    response: EditProfileFormMutation$data,
    errors: readonly PayloadError[] | null | undefined,
  ) => {
    if (!errors) {
      sendTrackEvent({
        source: SCREEN_NAME.EDIT_PROFILE,
        action: 'updated',
        actionSubject: 'profile',
        attributes: getTrackAttributes(response),
        isPersonalView: true,
      });
      push(`/profile/${pathProfileId}`);
    }
    onFinishEdit();
  };

  const toggleModal = useCallback(
    (modalShouldBeOpen?: boolean) => {
      if (modalShouldBeOpen !== undefined) {
        setIsModalOpen(modalShouldBeOpen);
      } else {
        setIsModalOpen(!isModalOpen);
      }
    },
    [isModalOpen],
  );

  const data = useLazyLoadQuery<EditProfileFormQuery>(
    EDIT_PROFILE_FORM_QUERY,
    { internalId: pathProfileId as string },
    { fetchPolicy: 'store-or-network' },
  );

  const unifiedProfile = data?.unified?.unifiedProfile;
  const unifiedProfileDetailsData =
    unifiedProfile && `__fragments` in unifiedProfile
      ? readInlineData<UnifiedProfileFragment$key>(
          UNIFIED_PROFILE_FRAGMENT,
          unifiedProfile,
        )
      : null;

  const atlassianProducts = data?.unified?.atlassianProducts;
  const productData: AtlassianProductsFragment$data | null =
    atlassianProducts && `__fragments` in atlassianProducts
      ? useFragment<AtlassianProductsFragment$key>(
          ATLASSIAN_PRODUCTS_FRAGMENT,
          atlassianProducts,
        )
      : null;

  const allProducts: NormalizedProduct[] =
    productData && productData.edges ? useNormalizedProducts(productData) : [];

  const atlassianUserDetailsData =
    useFragment<AtlassianAccountUserFragment$key>(
      ATLASSIAN_ACCOUNT_USER_FRAGMENT,
      data?.me?.user,
    );

  function getPrimaryAccountDetails(
    account: UnifiedAccountQuery['response']['unified'],
    defaults: {
      picture?: string;
      nickname?: string;
      email?: string;
    } = {},
  ): {
    primaryAccountEmail: string;
    primaryAccountPicture: string;
    primaryAccountNickname: string;
    primaryAccountAaid: string | false;
  } {
    const defaultResult = {
      primaryAccountEmail: defaults.email || '',
      primaryAccountPicture: defaults.picture || '',
      primaryAccountNickname: defaults.nickname || '',
      primaryAccountAaid: false as string | false,
    };
    if (account?.account?.isLinked) {
      const primaryAccount = account.account.linkedAccounts?.edges?.find(
        (edge) => edge?.node?.isPrimary === true,
      );
      if (primaryAccount?.node) {
        return {
          primaryAccountEmail:
            primaryAccount.node.emailId || defaults.email || '',
          primaryAccountPicture:
            primaryAccount.node.picture || defaults.picture || '',
          primaryAccountNickname:
            primaryAccount.node.nickname || defaults.nickname || '',
          primaryAccountAaid: primaryAccount.node.aaid || false,
        };
      }
    }
    return defaultResult;
  }

  const primaryAccountData = getPrimaryAccountDetails(account);
  const primaryAccountAaid = primaryAccountData.primaryAccountAaid;

  let primaryUserResponse;

  if (account?.account?.isLinked && primaryAccountAaid) {
    primaryUserResponse = useLazyLoadQuery<AtlassianAccountAnyUserQuery>(
      ATLASSIAN_ACCOUNT_ANY_USER_QUERY,
      { accountId: primaryAccountAaid },
      { fetchPolicy: 'store-or-network' },
    );
  }

  const primaryUserData = primaryUserResponse?.user;
  const primaryUserNickname = primaryUserData?.nickname ?? null;
  const primaryUserPicture = primaryUserData?.picture ?? null;

  const primaryAccountPicture = primaryAccountData.primaryAccountPicture;
  const primaryAccountNickname = primaryAccountData.primaryAccountNickname;

  const accountEmail =
    primaryAccountData.primaryAccountEmail ||
    atlassianUserDetailsData?.email ||
    '';

  let accountPicture: string;
  let accountNickname: string;
  if (isProfilePersonalView) {
    accountPicture =
      primaryAccountPicture ||
      atlassianUserDetailsData?.picture ||
      ProfilePhoto;
    accountNickname =
      primaryAccountNickname || atlassianUserDetailsData?.nickname || '';
  } else {
    accountPicture =
      primaryUserPicture || atlassianUserDetailsData?.picture || ProfilePhoto;
    accountNickname =
      primaryUserNickname || atlassianUserDetailsData?.nickname || '';
  }

  if (!unifiedProfile || !unifiedProfileDetailsData) {
    return <div>{'Could not fetch profile data'}</div>;
  }

  if (!atlassianUserDetailsData) {
    return <div>{'Could not fetch user data'}</div>;
  }

  let manageIdentityProfile = process.env.REACT_APP_IDENTITY_MANAGE_PROFILE_URL;
  if (accountEmail) {
    manageIdentityProfile += `&login_hint=${encodeURIComponent(
      accountEmail,
    )}&prompt=none&application=community--entry-auth`;
  }

  const validateBio = (value?: string) => {
    if (value) {
      const text = getTextOnly(value);
      return InputValidator.characterLimit(200, text);
    }
    return undefined;
  };

  async function commitAddUser(
    input: UnifiedProfileInput,
    onCompleted: (
      response: EditProfileFormMutation$data,
      errors: readonly PayloadError[] | null | undefined,
    ) => void,
    onError: (error: Error) => void,
  ) {
    if (unifiedProfile) {
      input.internalId = unifiedProfileDetailsData?.internalId;
      commitMutation<EditProfileFormMutation>(environment, {
        mutation: CREATE_PROFILE_FORM_MUTATION,
        variables: {
          input,
        },
        onCompleted: onCompleted,
        onError: onError,
      });
    }
  }

  const onSubmitForm = (data: EditProfileFormValues) => {
    sendUIEvent({
      source: SCREEN_NAME.EDIT_PROFILE,
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: 'saveEditProfileChanges',
      isPersonalView: true,
    });

    if (data?.bio) {
      const bioText = getTextOnly(data.bio);
      if (!bioText?.trim()) {
        data.bio = '';
      }
    }
    const hasNonEmptyField = Object.values(data).some((value) => {
      if (typeof value === 'string') {
        return value.trim() !== '';
      }
      return false;
    });
    if (hasNonEmptyField) {
      const userId = atlassianUserDetailsData?.accountId;
      if (userId) {
        sendUserAction(userId, { name: 'PROFILE_EDITED' });
      }
    }
    commitAddUser(data, onSuccess, (error: Error) => {
      console.error('Error: ', error);
      addErrorFlag();
    });
  };

  return (
    <div css={containerStyles}>
      <UnstyledButton onClick={onClickBackToProfile}>
        <p css={backToProfileTextStyles}>{'← Back to profile'}</p>
      </UnstyledButton>
      <h2 css={editProfileHeaderStyles}>{'Edit profile'}</h2>
      <div css={formContainerStyles}>
        <Form<EditProfileFormValues> onSubmit={onSubmitForm}>
          {({ formProps }) => (
            <form {...formProps}>
              <div data-testid="edit-about-me-section">
                <h3 css={sectionHeaderStyles}>{'Name and photo'}</h3>
                {accountHasLinkedAccounts && (
                  <div css={infoPanelStyle}>
                    <SectionMessage>
                      {
                        'Your public name, photo, and email are controlled by your primary Atlassian account. '
                      }
                    </SectionMessage>
                  </div>
                )}

                <div css={identityCardStyles}>
                  <div css={[rowStyles, identityCardRowStyles]}>
                    <div
                      css={[
                        columnStyles,
                        mobileColumnStyles,
                        mobileProfilePhotoSectionStyles,
                      ]}
                    >
                      <div css={profilePhotoLabelWrapper}>
                        <TextFieldLabel
                          labelText={'Profile photo'}
                          isRequired={false}
                        />
                      </div>
                      <Image
                        src={accountPicture}
                        css={profilePhotoStyles}
                        role="img"
                      ></Image>
                      <div css={editInAccountButton}>
                        <UnstyledButton
                          type="button"
                          onClick={() => {
                            window.open(manageIdentityProfile, '_blank');
                          }}
                        >
                          <p css={editOnIdentityTextStyles}>
                            {'Edit in Atlassian account'}
                          </p>
                          <img
                            src={Offsite}
                            alt={'offsite'}
                            css={editOnIdentityIconStyles}
                          ></img>
                        </UnstyledButton>
                      </div>
                    </div>
                    <div css={columnStyles}>
                      {accountHasLinkedAccounts && (
                        <div css={primaryAccountTagStyle}>
                          <UnifiedAccountTagPrimary />
                        </div>
                      )}
                      <TextFieldLabel
                        labelText={'Public name'}
                        isRequired={true}
                      />
                      <TextField
                        isDisabled={true}
                        isInvalid={false}
                        value={accountNickname}
                      />
                      <div css={primaryEmailField}>
                        <TextFieldLabel labelText={'Email'} isRequired={true} />
                        <TextField
                          isDisabled={true}
                          isInvalid={false}
                          value={accountEmail}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div css={sectionContainerStyles}>
                  <h3 css={sectionHeaderStyles}>{'About me'}</h3>
                  {accountHasLinkedAccounts && (
                    <div css={infoPanelStyle}>
                      <SectionMessage>
                        {
                          'Changes to sections below will be reflected across all your linked accounts.'
                        }
                      </SectionMessage>
                    </div>
                  )}
                  <div data-testid="edit-current-job-section" css={rowStyles}>
                    <div css={[columnStyles, mobileRowStyles]}>
                      <Field
                        testId="role-input"
                        name="role"
                        defaultValue={unifiedProfileDetailsData?.role || ''}
                        validate={(value) =>
                          InputValidator.characterLimit(40, value)
                        }
                      >
                        {({ fieldProps }) => (
                          <div css={removeFieldMarginStyles}>
                            <TextFieldLabel
                              labelText={'Current position'}
                              isRequired={fieldProps.isRequired}
                            />
                            <TextField {...fieldProps} />
                            {fieldProps.isInvalid && (
                              <TextFieldMessage
                                type="error"
                                message="Current position must be 40 characters or less."
                              />
                            )}
                          </div>
                        )}
                      </Field>
                    </div>
                    <div css={[columnStyles, mobileRowStyles]}>
                      <Field
                        testId="company-input"
                        name="company"
                        defaultValue={unifiedProfileDetailsData?.company || ''}
                        validate={(value) =>
                          InputValidator.characterLimit(30, value)
                        }
                      >
                        {({ fieldProps }) => (
                          <div css={removeFieldMarginStyles}>
                            <TextFieldLabel
                              labelText="Company"
                              isRequired={fieldProps.isRequired}
                            />
                            <TextField {...fieldProps} />
                            {fieldProps.isInvalid && (
                              <TextFieldMessage
                                type="error"
                                message="Company must be 30 characters or less."
                              />
                            )}
                          </div>
                        )}
                      </Field>
                    </div>
                  </div>
                  <div
                    data-testid="edit-location-details-section"
                    css={rowStyles}
                  >
                    <div css={[columnStyles, mobileRowStyles]}>
                      <Field
                        testId="location-input"
                        name="location"
                        defaultValue={unifiedProfileDetailsData?.location || ''}
                        validate={(value) =>
                          InputValidator.characterLimit(30, value)
                        }
                      >
                        {({ fieldProps }) => (
                          <div css={removeFieldMarginStyles}>
                            <TextFieldLabel
                              labelText="Location"
                              isRequired={fieldProps.isRequired}
                            />
                            <TextField {...fieldProps} />
                            {fieldProps.isInvalid && (
                              <TextFieldMessage
                                type="error"
                                message="Location must be 30 characters or less."
                              />
                            )}
                          </div>
                        )}
                      </Field>
                    </div>
                    <div css={[columnStyles, mobileRowStyles]}></div>
                  </div>
                  <div data-testid="edit-bio-section" css={bioRowStyles}>
                    <Field
                      name="bio"
                      defaultValue={unifiedProfileDetailsData?.bio || ''}
                      validate={validateBio}
                    >
                      {({ fieldProps }) => (
                        <Box
                          data-name={fieldProps.id}
                          data-value={fieldProps.value}
                        >
                          <TextFieldLabel
                            labelText="Short bio"
                            isRequired={fieldProps.isRequired}
                          />
                          <TextEditor
                            changeHandler={fieldProps.onChange}
                            defaultValue={fieldProps.value}
                            isInvalid={fieldProps.isInvalid}
                          />
                          {fieldProps.isInvalid && (
                            <TextFieldMessage
                              type="error"
                              message="Bio must be 200 characters or less."
                            />
                          )}
                        </Box>
                      )}
                    </Field>
                  </div>
                </div>
              </div>

              <div
                data-testid="edit-contacts-section"
                css={sectionContainerStyles}
              >
                <h3 css={sectionHeaderStyles}>{'Where to find me'}</h3>
                <div css={rowStyles}>
                  <div css={[columnStyles, mobileRowStyles]}>
                    <Field
                      testId="websiteUrl-input"
                      name="websiteUrl"
                      defaultValue={unifiedProfileDetailsData?.websiteUrl || ''}
                      validate={(value) => InputValidator.validateUrl(value)}
                    >
                      {({ fieldProps }) => (
                        <div css={removeFieldMarginStyles}>
                          <TextFieldLabel
                            labelText="My website"
                            isRequired={fieldProps.isRequired}
                          />
                          <TextField {...fieldProps} />
                          {fieldProps.isInvalid && (
                            <TextFieldMessage
                              type="error"
                              message="Enter a valid URL."
                            />
                          )}
                        </div>
                      )}
                    </Field>
                  </div>
                  <div css={[columnStyles, mobileRowStyles]}>
                    <Field
                      testId="linkedinUrl-input"
                      name="linkedinUrl"
                      defaultValue={
                        unifiedProfileDetailsData?.linkedinUrl || ''
                      }
                      validate={(value) => InputValidator.validateUrl(value)}
                    >
                      {({ fieldProps }) => (
                        <div css={removeFieldMarginStyles}>
                          <TextFieldLabel
                            labelText="LinkedIn"
                            isRequired={fieldProps.isRequired}
                          />
                          <TextField
                            {...fieldProps}
                            placeholder="linkedin.com/in/"
                          />
                          {fieldProps.isInvalid && (
                            <TextFieldMessage
                              type="error"
                              message="Enter a valid LinkedIn URL."
                            />
                          )}
                        </div>
                      )}
                    </Field>
                  </div>
                </div>
                <div css={[rowStyles, secondRowDesktopStyles]}>
                  <div css={[columnStyles, mobileRowStyles]}>
                    <Field
                      testId="youtubeUrl-input"
                      name="youtubeUrl"
                      defaultValue={unifiedProfileDetailsData?.youtubeUrl || ''}
                      validate={(value) => InputValidator.validateUrl(value)}
                    >
                      {({ fieldProps }) => (
                        <div css={removeFieldMarginStyles}>
                          <TextFieldLabel
                            labelText="YouTube"
                            isRequired={fieldProps.isRequired}
                          />
                          <TextField
                            {...fieldProps}
                            placeholder="youtube.com/"
                          />
                          {fieldProps.isInvalid && (
                            <TextFieldMessage
                              type="error"
                              message="Enter a valid YouTube URL."
                            />
                          )}
                        </div>
                      )}
                    </Field>
                  </div>
                  <div css={columnStyles}>
                    {/* <Field
                      testId="xUrl-input"
                      name="xUrl"
                      defaultValue={unifiedProfileDetailsData?.xUrl || ''}
                      validate={(value) => InputValidator.validateUrl(value)}
                    >
                      {({ fieldProps }) => (
                        <div css={removeFieldMarginStyles}>
                          <TextFieldLabel
                            labelText="X (formerly known as Twitter)"
                            isRequired={fieldProps.isRequired}
                          />
                          <TextField {...fieldProps} placeholder="x.com/" />
                          {fieldProps.isInvalid && (
                            <TextFieldMessage
                              type="error"
                              message="Enter a valid X URL."
                            />
                          )}
                        </div>
                      )}
                    </Field> */}
                  </div>
                </div>
              </div>
              <div
                data-testid="edit-products-section"
                css={[myProductsSectionContainerStyles, finalMobileSection]}
              >
                <h3 css={[sectionHeaderStyles, productsHeaderStyles]}>
                  {'My products'}
                </h3>
                <div css={productsContentStyles}>
                  <ProductsContent
                    allProducts={allProducts}
                    setHasSelectedProducts={setHasSelectedProducts}
                  />
                </div>
                <Button
                  type="button"
                  appearance={ButtonAppearance.SECONDARY}
                  label={
                    hasSelectedProducts
                      ? 'Manage my products'
                      : 'Add my products'
                  }
                  onClick={(e) => {
                    e.preventDefault();
                    openModal();
                  }}
                />
              </div>

              <Field
                name="products"
                defaultValue={unifiedProfileDetailsData.products ?? null}
              >
                {({ fieldProps }) => (
                  <CommonModal
                    isOpen={isModalOpen}
                    toggleModal={toggleModal}
                    modalDescriptionLabel="Manage your products"
                    styles={modalStyles}
                  >
                    <ManageProducts
                      preselectedProducts={fieldProps.value}
                      allProducts={allProducts}
                      changeHandler={fieldProps.onChange}
                      closeModal={closeModal}
                    />
                  </CommonModal>
                )}
              </Field>

              <div
                data-testid="profile-privacy-section"
                css={sectionContainerStyles}
              >
                <h3 css={[sectionHeaderStyles, productsHeaderStyles]}>
                  {'Visibility settings'}
                </h3>
                <Box paddingBlockEnd="space.200">
                  <Text>{`Public profiles are visible to all users.`}</Text>
                </Box>
                <Text>{`If your Community profile is private:`}</Text>
                <ul css={visibilitySettingsList}>
                  <li>
                    <Text>{`Your public name and Community Forums activity (posts, likes, and comments) will remain public.`}</Text>
                  </li>
                  <li>
                    <Text>{`Your profile photo will respect your Atlassian account visibility settings*.`}</Text>
                  </li>
                  <li>
                    <Text>{`Your Community user data (Community stats, certifications, badges, kudos, groups) will only be visible to you.`}</Text>
                  </li>
                </ul>
                <Text
                  fontWeight="semibold"
                  size="small"
                >{`*If there is no profile photo in your Atlassian account, your Community Forum profile photo will default to an avatar containing your full name initials.`}</Text>
                <Field
                  name="isPrivate"
                  defaultValue={unifiedProfileDetailsData?.isPrivate ?? false}
                >
                  {({ fieldProps }) => (
                    <div css={radioButtonContainer} role="radiogroup">
                      <Radio
                        isChecked={!fieldProps.value}
                        setIsChecked={() => fieldProps.onChange(false)}
                        label={'Public'}
                      />
                      <div css={radioButtonSpacer} />
                      <Radio
                        isChecked={fieldProps.value}
                        setIsChecked={() => fieldProps.onChange(true)}
                        label={'Private'}
                      />
                    </div>
                  )}
                </Field>
              </div>
              <div css={bottomBarStyles}>
                <div css={formButtonsStyles}>
                  <UnstyledButton
                    onClick={onClickClose}
                    style={cancelButtonStyles}
                    type="button"
                  >
                    <p css={closeButtonStyles}>{'Close'}</p>
                  </UnstyledButton>
                  <Button
                    appearance={ButtonAppearance.PRIMARY}
                    label="Save changes"
                    type="submit"
                  />
                </div>
              </div>
            </form>
          )}
        </Form>
      </div>
    </div>
  );
};

export default EditProfileForm;

const radioButtonContainer = css({
  marginTop: '40px',
});

const primaryEmailField = css({
  marginTop: '24px',
});

const primaryAccountTagStyle = css({
  display: 'flex',
  justifyContent: 'flex-end',
});

const editInAccountButton = css({
  marginTop: token('space.300'),
});

const profilePhotoLabelWrapper = css({
  marginBottom: token('space.100'),
});

const visibilitySettingsList = css({
  marginBottom: '16px',
  paddingLeft: '16px',
  listStylePosition: 'inside',
  rowGap: '8px',
  display: 'inline-grid',
});

const radioButtonSpacer = css({
  marginTop: '16px',
});

const infoPanelStyle = css({
  marginBottom: token('space.200'),
});

const containerStyles = css({
  width: '100%',
  padding: '32px 16px 100px',
  '@media (min-width: 768px)': {
    width: '720px',
    margin: '40px auto 0 auto',
    padding: '40px 0 200px',
  },
});

const backToProfileTextStyles = css({
  color: token('color.text.brand'),
  fontFamily: token('font.family.brand.body'),
  fontSize: '16px',
  fontWeight: token('font.weight.semibold'),
  lineHeight: '20px',
});

const editProfileHeaderStyles = css({
  color: token('color.text'),
  fontFamily: token('font.family.brand.heading'),
  // MOBILE H2 BOLD
  marginTop: '24px',
  fontSize: '32px',
  fontWeight: token('font.weight.bold'),
  lineHeight: '40px',
  letterSpacing: '-0.096px',
  // DESKTOP H2 BOLD
  '@media (min-width: 768px)': {
    marginTop: '40px',
    fontSize: token('space.500'),
    lineHeight: token('space.600'),
    fontWeight: token('font.weight.bold'),
  },
});

const formContainerStyles = css({
  marginTop: '24px',
  '@media (min-width: 768px)': {
    marginTop: '40px',
  },
});

const removeFieldMarginStyles = css({
  '@media (min-width: 768px)': {
    marginTop: '-8px',
  },
});

const sectionContainerStyles = css({
  borderTop: `1px solid ${token('color.border')}`,
  padding: '32px 0',
  '@media (min-width: 768px)': {
    padding: '40px 0',
  },
});

const myProductsSectionContainerStyles = css({
  borderTop: `1px solid ${token('color.border')}`,
  paddingTop: '32px',
  '@media (min-width: 768px)': {
    paddingTop: '40px',
    //Margin built into collapsed modal, created this class to offset that (8px).
    paddingBottom: '32px',
  },
  '@media (max-width: 768px)': {
    marginBottom: '32px',
  },
});

const finalMobileSection = css({
  '@media (max-width: 768px)': {
    paddingBottom: '0px',
  },
});

const sectionHeaderStyles = css({
  marginBottom: '16px',
  fontFamily: token('font.family.brand.heading'),
  fontWeight: token('font.weight.semibold'),
  color: token('color.text'),
  // Mobile H3 Semibold
  fontSize: '24px',
  lineHeight: '32px',
  // Desktop H3 Semibold
  '@media (min-width: 768px)': {
    fontSize: '32px',
    lineHeight: token('space.500'),
    fontFamily: token('font.family.brand.heading'),
  },
});

const identityCardStyles = css({
  borderRadius: token('space.100'),
  boxShadow: token('elevation.shadow.raised'),
  marginBottom: '32px',
  padding: '16px',
  '@media (min-width: 768px)': {
    padding: '24px',
    marginBottom: '40px',
  },
});

const mobileProfilePhotoSectionStyles = css({
  '@media (max-width: 767px)': {
    marginBottom: '16px',
  },
});

const editOnIdentityTextStyles = css({
  fontFamily: token('font.family.brand.body'),
  fontSize: '16px',
  color: token('color.text.brand'),
  fontWeight: token('font.weight.semibold'),
  lineHeight: '20px',
  marginRight: '4px',
});

const editOnIdentityIconStyles = css({
  width: '24px',
  height: '24px',
});

const rowStyles = css({
  '@media (min-width: 768px)': {
    display: 'flex',
    width: '100%',
    justifyContent: 'space-between',
    marginBottom: '40px',
    gap: '48px',
  },
});

const secondRowDesktopStyles = css({
  '@media (min-width: 768px)': {
    marginBottom: '0px',
  },
});

const identityCardRowStyles = css({
  '@media (min-width: 768px)': {
    marginBottom: '0px',
  },
});

const mobileRowStyles = css({
  '@media (max-width: 768px)': {
    marginBottom: '24px',
  },
});

const bioRowStyles = css({
  width: '100%',
});

const columnStyles = css({
  '@media (min-width: 768px)': {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
});

const mobileColumnStyles = css({
  '@media (max-width: 767px)': {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
});

const profilePhotoStyles = css({
  width: '96px',
  height: '96px',
  borderRadius: '50%',
});

const productsHeaderStyles = css({
  marginBottom: '12px',
  '@media (min-width: 768px)': {
    marginBottom: '16px',
  },
});

const productsQuestionStyles = css({
  fontFamily: 'inter',
  fontSize: token('space.200'),
  lineHeight: '24px',
});

const productsContentStyles = css({
  marginBottom: token('space.300'),
  '@media (min-width: 768px)': {
    marginBottom: token('space.500'),
  },
});

const modalStyles = cssEmotion({
  padding: '0',
  borderRadius: '0',
  height: '100%',
  '@media (max-width: 767px)': {
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    '>div': {
      borderRadius: '0',
      height: '100%',
      width: '100%',
      padding: `48px ${token('space.200')} ${token('space.200')}`,
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    '>div>div': {
      height: '100%',
    },
  },
});

const bottomBarStyles = css({
  position: 'fixed',
  bottom: '0',
  left: '0',
  height: '72px',
  width: '100%',
  backgroundColor: token('color.text.inverse'),
  boxShadow: token('elevation.shadow.overflow'),
});

const formButtonsStyles = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  height: '100%',
  margin: 'auto 16px auto auto',
  '@media (min-width: 768px)': {
    width: '720px',
    margin: 'auto',
  },
});

const cancelButtonStyles = {
  padding: `${token('space.100')} ${token('space.150')}`,
  marginRight: token('space.300'),
};

const closeButtonStyles = css({
  color: token('color.text.brand'),
  fontFamily: token('font.family.brand.body'),
  fontSize: '16px',
  fontWeight: token('font.weight.semibold'),
  lineHeight: '20px',
});
